<template>
    <vx-expandable-card>
        <template v-slot:header> Estimated Throughput </template>

        <template v-if="listError" v-slot:content>
            <vx-alert :closeable="false" color="danger" shadow class="mt-3">
                {{ listError }}
            </vx-alert>
        </template>
        <template v-else-if="loading" v-slot:content>
            <page-loading class="py-8"></page-loading>
        </template>
        <template v-else v-slot:content>
            <div class="flex flex-col gap-6 mt-3">
                <!-- Header with Adjust/Save/Cancel buttons -->
                <div v-if="isEditable" class="h-10 flex justify-between items-center">
                    <div class="flex-1"></div>
                    <div v-if="!isEditing">
                        <vx-button size="sm" color="primary" @click="startEditing" data-test="adjust-button">
                            Adjust
                        </vx-button>
                    </div>
                    <div v-else class="flex gap-2">
                        <vx-button
                            size="sm"
                            color="primary"
                            @click="saveThroughput"
                            :loading="submitting"
                            data-test="save-button"
                        >
                            Save Changes
                        </vx-button>
                        <vx-button
                            size="sm"
                            color="muted"
                            @click="cancelEditing"
                            :disabled="submitting"
                            data-test="cancel-button"
                        >
                            Cancel
                        </vx-button>
                    </div>
                </div>

                <!-- Info message when editing -->
                <div v-if="isEditing" class="mt-3 mx-4">
                    <vx-alert color="warning" :closeable="false">
                        <template v-slot:icon>
                            <font-awesome-icon widthAuto :icon="faCircleInfo" />
                        </template>
                        These rates are only used for blast campaign capacity estimation purposes and do not actually
                        control send rate.
                    </vx-alert>
                </div>

                <!-- 10DLC Section -->
                <div>
                    <div class="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 px-2">
                        10DLC Numbers
                    </div>
                    <div class="rounded-lg border border-gray-200 bg-white transition-all duration-200">
                        <div class="divide-y divide-gray-200">
                            <!-- 10DLC SMS Row -->
                            <div class="flex justify-between items-center px-6" :class="isEditing ? 'py-3' : 'py-4'">
                                <span class="text-gray-700">SMS MPS</span>
                                <div class="flex flex-col items-end">
                                    <span v-if="!isEditing" class="font-mono text-gray-900">
                                        {{ formatRate(throughputData.sms_10dlc) }}
                                    </span>
                                    <vx-input
                                        v-else
                                        v-model="editingValues.sms_10dlc"
                                        type="number"
                                        step="0.01"
                                        min="0.01"
                                        max="9999.99"
                                        size="sm"
                                        class="w-32 text-right font-mono"
                                        placeholder="0.00"
                                        @blur="formatEditingValue('sms_10dlc')"
                                        :error="validationErrors.sms_10dlc"
                                        data-test="input-sms-10dlc"
                                    />
                                </div>
                            </div>

                            <!-- 10DLC MMS Row -->
                            <div class="flex justify-between items-center px-6" :class="isEditing ? 'py-3' : 'py-4'">
                                <span class="text-gray-700">MMS MPS</span>
                                <div class="flex flex-col items-end">
                                    <span v-if="!isEditing" class="font-mono text-gray-900">
                                        {{ formatRate(throughputData.mms_10dlc) }}
                                    </span>
                                    <vx-input
                                        v-else
                                        v-model="editingValues.mms_10dlc"
                                        type="number"
                                        step="0.01"
                                        min="0.01"
                                        max="9999.99"
                                        size="sm"
                                        class="w-32 text-right font-mono"
                                        placeholder="0.00"
                                        @blur="formatEditingValue('mms_10dlc')"
                                        :error="validationErrors.mms_10dlc"
                                        data-test="input-mms-10dlc"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Tollfree Section -->
                <div>
                    <div class="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 px-2">
                        Tollfree Numbers
                    </div>
                    <div class="rounded-lg border border-gray-200 bg-white transition-all duration-200">
                        <div class="divide-y divide-gray-200">
                            <!-- Tollfree SMS Row -->
                            <div class="flex justify-between items-center px-6" :class="isEditing ? 'py-3' : 'py-4'">
                                <span class="text-gray-700">SMS MPS</span>
                                <div class="flex flex-col items-end">
                                    <span v-if="!isEditing" class="font-mono text-gray-900">
                                        {{ formatRate(throughputData.sms_tollfree) }}
                                    </span>
                                    <vx-input
                                        v-else
                                        v-model="editingValues.sms_tollfree"
                                        type="number"
                                        step="0.01"
                                        min="0.01"
                                        max="9999.99"
                                        size="sm"
                                        class="w-32 text-right font-mono"
                                        placeholder="0.00"
                                        @blur="formatEditingValue('sms_tollfree')"
                                        :error="validationErrors.sms_tollfree"
                                        data-test="input-sms-tollfree"
                                    />
                                </div>
                            </div>

                            <!-- Tollfree MMS Row -->
                            <div class="flex justify-between items-center px-6" :class="isEditing ? 'py-3' : 'py-4'">
                                <span class="text-gray-700">MMS MPS</span>
                                <div class="flex flex-col items-end">
                                    <span v-if="!isEditing" class="font-mono text-gray-900">
                                        {{ formatRate(throughputData.mms_tollfree) }}
                                    </span>
                                    <vx-input
                                        v-else
                                        v-model="editingValues.mms_tollfree"
                                        type="number"
                                        step="0.01"
                                        min="0.01"
                                        max="9999.99"
                                        size="sm"
                                        class="w-32 text-right font-mono"
                                        placeholder="0.00"
                                        @blur="formatEditingValue('mms_tollfree')"
                                        :error="validationErrors.mms_tollfree"
                                        data-test="input-mms-tollfree"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script setup>
import { VxExpandableCard, VxInput, VxButton, VxAlert } from '@voxie/frontend-components';
import { adminTeamsClient } from '~/services';
import PageLoading from '~/components/general/PageLoading.vue';
import { onMounted, ref } from 'vue';
import { HTTP_INTERNAL_SERVER_ERROR } from '~/support/HttpStatuses';
import { useToasted } from '~/composables/useToasted';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
    isEditable: {
        type: Boolean,
        default: true,
    },
});

const toasted = useToasted();
const { catchError } = useFormRequestHandler();
const loading = ref(true);
const submitting = ref(false);
const listError = ref('');
const isEditing = ref(false);
const editingValues = ref({});
const originalValues = ref({});
const validationErrors = ref({});

const throughputData = ref({
    sms_10dlc: 0,
    mms_10dlc: 0,
    sms_tollfree: 0,
    mms_tollfree: 0,
});

const formatRate = (value) => {
    return parseFloat(value || 0).toFixed(2);
};

const formatEditingValue = (field) => {
    const value = parseFloat(editingValues.value[field]);
    if (!isNaN(value)) {
        editingValues.value[field] = value.toFixed(2);
    }
};

const validateEditingValues = () => {
    const errors = {};
    const maxValue = 9999.99;

    Object.keys(editingValues.value).forEach((field) => {
        const value = parseFloat(editingValues.value[field]);

        if (isNaN(value)) {
            errors[field] = 'Invalid number';
        } else if (value < 0) {
            errors[field] = 'Cannot be negative';
        } else if (value > maxValue) {
            errors[field] = `Cannot exceed ${maxValue}`;
        }
    });

    validationErrors.value = errors;
    return Object.keys(errors).length === 0;
};

const startEditing = () => {
    editingValues.value = {
        sms_10dlc: throughputData.value.sms_10dlc.toFixed(2),
        mms_10dlc: throughputData.value.mms_10dlc.toFixed(2),
        sms_tollfree: throughputData.value.sms_tollfree.toFixed(2),
        mms_tollfree: throughputData.value.mms_tollfree.toFixed(2),
    };

    originalValues.value = { ...editingValues.value };
    validationErrors.value = {};
    isEditing.value = true;
};

const cancelEditing = () => {
    isEditing.value = false;
    editingValues.value = {};
    originalValues.value = {};
    validationErrors.value = {};
};

const saveThroughput = async () => {
    if (submitting.value) {
        return;
    }

    if (!validateEditingValues()) {
        return;
    }

    submitting.value = true;

    try {
        const promises = [
            adminTeamsClient.updateThroughput(props.teamId, '10dlc_sms', editingValues.value.sms_10dlc),
            adminTeamsClient.updateThroughput(props.teamId, '10dlc_mms', editingValues.value.mms_10dlc),
            adminTeamsClient.updateThroughput(props.teamId, 'tf_sms', editingValues.value.sms_tollfree),
            adminTeamsClient.updateThroughput(props.teamId, 'tf_mms', editingValues.value.mms_tollfree),
        ];

        await Promise.all(promises);

        // Refetch data to ensure consistency
        await fetchThroughput(false);

        toasted.success('Estimated throughput settings have been updated');
        isEditing.value = false;
        editingValues.value = {};
        originalValues.value = {};
        validationErrors.value = {};
    } catch (e) {
        console.error(e);
        catchError(e);
    } finally {
        submitting.value = false;
    }
};

const fetchThroughput = async (showLoading = true) => {
    if (showLoading) {
        loading.value = true;
    }

    try {
        const { data } = await adminTeamsClient.getThroughputs(props.teamId);

        throughputData.value = {
            sms_10dlc: data.data.find((t) => t.throughput_type === '10dlc_sms')?.throughput_rate || 0,
            mms_10dlc: data.data.find((t) => t.throughput_type === '10dlc_mms')?.throughput_rate || 0,
            sms_tollfree: data.data.find((t) => t.throughput_type === 'tf_sms')?.throughput_rate || 0,
            mms_tollfree: data.data.find((t) => t.throughput_type === 'tf_mms')?.throughput_rate || 0,
        };
    } catch (e) {
        console.error(e);
        if (!e?.response?.status || e.response.status >= HTTP_INTERNAL_SERVER_ERROR) {
            listError.value = 'Something went wrong.';
        } else {
            listError.value = e.response?.data?.message || 'Something went wrong.';
        }
    } finally {
        if (showLoading) {
            loading.value = false;
        }
    }
};

onMounted(async () => {
    await fetchThroughput();
});
</script>
